import { Check, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Todo } from "@/types/todo";
import { useState } from "react";
interface PaywallContentProps {
  todos: Todo[];
  completedTodos: Todo[];
  isProcessingPayment: boolean;
  selectedAmount: number | null;
  onPayment: (option: any) => void;
  isTestMode?: boolean;
}
const PaywallContent = ({
  todos,
  completedTodos,
  isProcessingPayment,
  selectedAmount,
  onPayment,
  isTestMode = false,
}: PaywallContentProps) => {
  const overdueTasks = todos.filter((todo) => !todo.completed && new Date() > todo.deadline && !todo.isOverdrive);

  const [selectedPresetAmount, setSelectedPresetAmount] = useState<string>(completedTodos.length >= 50 ? "6" : "3");
  const [customAmount, setCustomAmount] = useState("");
  const [isCustomSelected, setIsCustomSelected] = useState(false);
  const [customAmountError, setCustomAmountError] = useState<string>("");

  const presetAmounts = [3, 6, 9];

  const getCoffeeText = (amount: number) => {
    const coffeeCount = amount / 3;
    return coffeeCount === 1 ? "1 coffee" : `${coffeeCount} coffees`;
  };

  const handlePresetAmountChange = (value: string) => {
    if (value) {
      setSelectedPresetAmount(value);
      setIsCustomSelected(false);
      setCustomAmount("");
    }
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    setSelectedPresetAmount("");
    setIsCustomSelected(true);

    // Validate the amount
    if (value) {
      const amount = parseFloat(value);
      if (isNaN(amount) || amount <= 0) {
        setCustomAmountError("Please enter a valid number");
      } else if (amount < 3) {
        setCustomAmountError("Minimum amount is $3.00");
      } else if (amount > 100) {
        setCustomAmountError("Maximum amount is $100.00");
      } else {
        setCustomAmountError("");
      }
    } else {
      setCustomAmountError("");
    }
  };

  const handleContinue = () => {
    const amount = isCustomSelected ? parseFloat(customAmount) : parseFloat(selectedPresetAmount);
    if (amount && amount > 0) {
      const paymentType = overdueTasks.length > 0 ? "clearOverdue" : "payment";
      onPayment({ amount, type: paymentType });
    }
  };

  const isValidAmount = () => {
    if (selectedPresetAmount) return true;
    if (isCustomSelected && customAmount && !customAmountError) {
      const amount = parseFloat(customAmount);
      return amount >= 3 && amount <= 100;
    }
    return false;
  };
  return (
    <div className="space-y-4 overflow-hidden min-w-0 px-1">
      {/* Overdue Tasks Display */}
      <div className="space-y-2 min-w-0">
        <p className="text-sm text-muted-foreground break-words">{overdueTasks.map((task) => task.title).join(", ")}</p>
      </div>

      {/* Payment Options */}
      <div className="space-y-3 min-w-0">
        <div className="pb-3">
          <h3 className="text-sm font-medium flex items-center gap-2">
            <Coffee className="h-4 w-4 flex-shrink-0" />
            Donate a coffee to continue and stay accountable
          </h3>
          <p className="text-sm text-muted-foreground break-words">Boost your motivation and support Countdown Todo.</p>
        </div>
        {/* Preset Amount Selection */}
        <ToggleGroup
          type="single"
          value={selectedPresetAmount}
          onValueChange={handlePresetAmountChange}
          className="rounded-md p-0.5 bg-muted grid grid-cols-3 gap-1"
        >
          {presetAmounts.map((amount, idx) => (
            <ToggleGroupItem
              key={amount}
              value={amount.toString()}
              disabled={isProcessingPayment}
              className="h-16 rounded-sm data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-[inset_0_0_0_1px] data-[state=on]:shadow-border font-semibold flex items-center justify-center relative"
            >
              {selectedPresetAmount === amount.toString() && (
                <Check className="h-4 w-4 text-green-500 absolute left-2" />
              )}
              <div className="flex flex-col items-center justify-center">
                <div className="text-base flex items-center gap-1">
                  {amount / 3} x <Coffee className="h-4 w-4" />
                </div>
                <div className="text-sm">${amount}</div>
              </div>
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        {/* Custom Amount Input */}
        <div className="space-y-1 pt-3 pb-3">
          <p className="text-sm text-muted-foreground">Or enter an amount</p>
          <div className="relative">
            <Input
              type="number"
              placeholder="Enter custom amount"
              value={customAmount}
              onChange={(e) => handleCustomAmountChange(e.target.value)}
              className={`rounded-xl pl-8 focus-visible:ring-0 ${
                isCustomSelected ? (customAmountError ? "border-destructive" : "border-primary") : "border-border"
              }`}
              disabled={isProcessingPayment}
            />
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">$</span>
            {isCustomSelected && customAmount && !customAmountError && (
              <Check className="h-4 w-4 absolute top-1 right-3 text-primary" />
            )}
          </div>
          {customAmountError && <p className="text-xs text-destructive mt-1">{customAmountError}</p>}
        </div>
      </div>

      {/* Continue Button */}
      <div className="mt-6">
        <Button onClick={handleContinue} disabled={isProcessingPayment || !isValidAmount()} className="w-full h-12">
          {isProcessingPayment
            ? "Processing..."
            : `Tip $${isCustomSelected ? customAmount : selectedPresetAmount} and Continue${isTestMode ? " (TEST)" : ""}`}
        </Button>
      </div>
    </div>
  );
};
export default PaywallContent;
